import { SheetOrDialog } from '@sravni/cosago-react-library/lib/components';
import { Heading } from '@sravni/react-design-system/lib/Typography';
import { type ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';

import { useAppSelector } from 'shared/lib';
import type { TPreApprovalOfferStepData } from 'shared/types/TRankingService';

import { selectMobilePhone } from 'entities/creditForm';

// eslint-disable-next-line boundaries/element-types
import { PreApprovalConfirmationForm, useInitialSendSmsConfirmation } from 'features/PreApprovalConfirmationForm';

import styles from './ConfirmationStep.module.scss';
import { ConfirmationStepTexts } from './ConfirmationStep.texts';

type Props = {
  offerData: TPreApprovalOfferStepData;
  onNext: () => void;
  onClose: () => void;
  onSmsCodeEvent?: ComponentProps<typeof PreApprovalConfirmationForm>['onSmsCodeEvent'];
  onShowEvent?: (offer: TPreApprovalOfferStepData) => void;
};

const CONFIRMATION_FORM_ID = 'confirmationForm';

export const ConfirmationStep: FC<Props> = (props) => {
  const { onNext, onClose, offerData, onSmsCodeEvent, onShowEvent } = props;
  const { preApprovalOfferId = '', bankName } = offerData;
  const phoneNumber = useAppSelector(selectMobilePhone);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const initialSmsSend = useInitialSendSmsConfirmation(preApprovalOfferId);

  const subtitleText = useMemo(() => {
    const subtitleCodeText = `${bankName} отправил код на ${phoneNumber}`;
    return `${ConfirmationStepTexts.popupSubTitle}\n ${subtitleCodeText}.`;
  }, [phoneNumber, bankName]);

  const initializeConfirmation = useCallback(async () => {
    const { isSendDoubleSms, isTooManyRequests } = await initialSmsSend();
    setIsDisabled(isTooManyRequests);
    if (isSendDoubleSms) {
      onNext();
    } else {
      onShowEvent?.(offerData);
      setIsInitialized(true);
    }
  }, [initialSmsSend, onNext, onShowEvent, offerData]);

  useEffect(() => {
    initializeConfirmation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isInitialized) {
    return null;
  }

  return (
    <SheetOrDialog
      visible
      onClose={onClose}
      className={styles.confirmationStep}
    >
      <SheetOrDialog.Header
        title={<Heading level={3}>{ConfirmationStepTexts.popupTitle}</Heading>}
        subtitle={subtitleText}
      />
      <SheetOrDialog.Content>
        <PreApprovalConfirmationForm
          formId={CONFIRMATION_FORM_ID}
          onFormSubmit={onNext}
          isDisabledByTooManyRequestsError={isDisabled}
          onDisableByTooManyRequests={() => setIsDisabled(true)}
          offerData={offerData}
          onSmsCodeEvent={onSmsCodeEvent}
        />
      </SheetOrDialog.Content>
    </SheetOrDialog>
  );
};
